/**
 * Human-readable labels for admin audit / IP logs (hide raw API paths & enum codes).
 */

const ROUTE_PATTERNS = [
  [/^\/api\/admin-auth\/login$/i, "Admin sign-in"],
  [/^\/api\/admin-auth\/logout$/i, "Admin sign-out"],
  [/^\/api\/admin-auth\/me$/i, "Session check"],
  [/^\/api\/admin-auth\/refresh$/i, "Session refresh"],
  [/^\/api\/admin\/dashboard/i, "Dashboard"],
  [/^\/api\/admin\/admins/i, "Admin management"],
  [/^\/api\/admin\/users/i, "User management"],
  [/^\/api\/admin\/security\/access-logs/i, "IP access logs"],
  [/^\/api\/admin\/security/i, "Security settings"],
  [/^\/api\/admin\/spot/i, "Spot trading"],
  [/^\/api\/admin\/wallet/i, "Wallet management"],
  [/^\/api\/admin\/p2p/i, "Disputes"],
  [/^\/api\/admin\/kyc/i, "KYC review"],
  [/^\/api\/admin\/otc/i, "OTC desk"],
  [/^\/api\/admin\/finance\/tds/i, "TDS & tax"],
  [/^\/api\/admin\/inr/i, "INR payments"],
  [/^\/api\/admin\/fees/i, "Fee management"],
  [/^\/api\/admin\/risk/i, "Risk management"],
  [/^\/api\/admin\/aml/i, "AML / FIU"],
  [/^\/api\/admin\/cms/i, "CMS & announcements"],
  [/^\/api\/admin\/support/i, "Support tickets"],
  [/^\/api\/admin\/reports/i, "Reports"],
  [/^\/api\/admin\/audit-log/i, "Activity logs"],
];

const IP_REASON_LABELS = {
  super_admin_bypass: "Super Admin — any location",
  whitelisted: "Allowed IP address",
  not_whitelisted: "IP not on allowlist",
  blacklisted: "Blocked IP (blacklist)",
  restriction_disabled: "IP restriction disabled",
  empty_allowed_list: "No IP list — access allowed",
  login_precheck_ok: "Sign-in permitted",
  rate_limited: "Too many sign-in attempts",
  no_admin_context: "Not signed in",
  api_restriction_disabled: "API IP check off",
  denied: "Access denied",
  blocked: "Blocked",
  allowed: "Allowed",
};

const AUDIT_ACTION_LABELS = {
  admin_invite: "Created admin account",
  admin_update: "Updated admin account",
  admin_login: "Signed in",
  admin_login_failed: "Failed sign-in",
  admin_login_ip_denied: "Sign-in blocked (IP)",
  admin_login_locked: "Sign-in locked",
  admin_login_blocked: "Sign-in blocked",
  user_suspend: "Suspended user",
  user_update: "Updated user",
  force_logout: "Forced user logout",
};

function titleCase(s) {
  return s
    .replace(/_/g, " ")
    .replace(/\//g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

/** e.g. GET /api/admin/dashboard/kpis → "Dashboard" */
export function formatAdminRoute(route, method) {
  if (!route) return "—";
  const path = String(route).split("?")[0];
  for (const [re, label] of ROUTE_PATTERNS) {
    if (re.test(path)) return label;
  }
  const stripped = path
    .replace(/^\/api\/admin-auth\/?/i, "")
    .replace(/^\/api\/admin\/?/i, "")
    .split("/")
    .filter(Boolean)[0];
  if (stripped) return titleCase(stripped);
  return titleCase(path.replace(/^\/api\//, ""));
}

/** Short line for table: "Dashboard · GET" */
export function formatAdminRouteLine(route, method) {
  const label = formatAdminRoute(route, method);
  const verb = method ? String(method).toUpperCase() : "";
  if (!verb || verb === "GET") return label;
  return `${label} · ${verb}`;
}

export function formatIpReason(reason) {
  if (!reason) return "—";
  return IP_REASON_LABELS[reason] || titleCase(reason);
}

export function formatAuditAction(action) {
  if (!action) return "—";
  return AUDIT_ACTION_LABELS[action] || titleCase(action);
}

export function formatAuditTarget(action, target) {
  if (!target) return "—";
  if (action === "admin_invite" || action === "admin_update") return target;
  return target;
}
